import React from "react";
import {
  X,
  Mail,
  Phone,
  Clock,
  User,
  CheckCircle2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Booking } from "../services/BookingsApi";
import { useAuth } from "../auth/AuthContext";
import { cn } from "../lib/utils";

interface BookingDetailsDrawerProps {
  isOpen: boolean;
  booking: Booking | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onDelete: (bookingId: number) => void;
  onEdit?: (bookingId: number) => void;
}

const BookingDetailsDrawer: React.FC<BookingDetailsDrawerProps> = ({
  isOpen,
  booking,
  loading,
  error,
  onClose,
}) => {
  const { user } = useAuth();
  const authCurrency = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "";

  const currency =
    booking?.rawData?.company?.currency?.currency_symbol ||
    booking?.rawData?.company?.currency?.currency_code ||
    authCurrency;

  const items = Array.isArray(booking?.rawData?.items)
    ? booking?.rawData?.items || []
    : [];

  // Build a lookup of employee id → name from the booking-level employees array
  // so we can resolve per-item stylist from item.employee_id
  const employeeLookup: Record<number, string> = {};
  if (Array.isArray((booking?.rawData as any)?.employees)) {
    (booking?.rawData as any).employees.forEach((emp: any) => {
      if (emp?.id && emp?.name) employeeLookup[Number(emp.id)] = emp.name;
    });
  }

  // Optimized helper to format time from various backend formats
  const formatTimeFromDateTime = (dateTime: string): string => {
    if (!dateTime) return "";
    try {
      // Check if it's already "21 Jan 2026 09:39 PM" format
      if (dateTime.match(/\d{1,2}:\d{2}\s+[AP]M/i)) {
        const parts = dateTime.split(" ");
        return `${parts[parts.length - 2]} ${parts[parts.length - 1]}`;
      }

      let cleanedTime = dateTime.replace(/\.?\d{0,6}Z?$/, '');
      const [dPart, tPart] = cleanedTime.split(/[T ]/);
      if (!dPart || !tPart) {
        const date = new Date(dateTime);
        if (!isNaN(date.getTime())) {
          return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
        }
        return dateTime;
      }
      const timeParts = tPart.split(":");
      const h = Number(timeParts[0] || 0);
      const m = Number(timeParts[1] || 0);
      const ampm = h >= 12 ? "PM" : "AM";
      const displayHours = h % 12 || 12;
      return `${displayHours}:${String(m).padStart(2, "0")} ${ampm}`;
    } catch (err) {
      return dateTime;
    }
  };

  const bookingTime = booking?.rawData?.date_time
    ? formatTimeFromDateTime(String(booking.rawData.date_time))
    : booking?.bookingTime?.split(" ").slice(1).join(" ") || "N/A";

  // Summary Calculations
  const subtotal = items.reduce((sum, item: any) => {
    const quantity = item?.qty || item?.quantity || 1;
    // For subtotal, we use the original price (if available) or unit_price
    const originalUnitPrice = Number(item?.business_service?.price || item?.unit_price || 0);
    return sum + (originalUnitPrice * quantity);
  }, 0);

  const totalDiscount = items.reduce((sum, item: any) => {
    const quantity = item?.qty || item?.quantity || 1;
    const originalPrice = Number(item?.business_service?.price || item?.unit_price || 0);
    const finalPrice = Number(item?.unit_price || 0);
    const itemDiscount = Math.max(0, originalPrice - finalPrice);
    return sum + (itemDiscount * quantity);
  }, 0);

  const totalTax = Number(booking?.rawData?.tax_amount || booking?.rawData?.total_tax || 0);
  const totalPaidAmount =
    Number(
      (booking?.rawData as any)?.total_paid ??
        (booking?.rawData as any)?.amountPaid ??
        booking?.paid ??
        0,
    ) || 0;
  const totalDueAmount =
    Number(
      (booking?.rawData as any)?.total_pending ??
        (booking?.rawData as any)?.pending_amount ??
        booking?.pending ??
        0,
    ) || 0;
  const totalAmount = Number(booking?.rawData?.amount_to_pay || (subtotal - totalDiscount + totalTax));

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[999]"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <div
        className={cn(
          "fixed top-0 right-0 h-full w-full max-w-5xl bg-white shadow-2xl z-[1000] transform transition-transform duration-300 ease-in-out flex flex-col",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        {isOpen && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="absolute lg:-left-14 top-4 lg:top-8 right-4 lg:right-auto w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-lg lg:shadow-[-4px_0_12px_rgba(0,0,0,0.1)] hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
          >
            <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
          </motion.button>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center bg-[#F9F9F9]">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-[#885ABB] border-t-transparent rounded-full animate-spin" />
              <p className="text-gray-500 font-medium whitespace-nowrap">Loading booking...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center bg-[#F9F9F9] p-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto">
                <X size={32} />
              </div>
              <h3 className="text-xl font-bold text-[#402B69]">Something went wrong</h3>
              <p className="text-gray-500 max-w-xs mx-auto">{error}</p>
              <button onClick={onClose} className="px-6 py-2 bg-[#885ABB] text-white rounded-xl font-bold">Close</button>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col lg:flex-row overflow-y-auto lg:overflow-hidden bg-[#F9F9F9] custom-scrollbar">
            {/* LEFT PANEL - Matching AppointmentSidebar logic/sizing */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="w-full lg:w-[320px] bg-white border-r border-gray-100 flex flex-col shrink-0 overflow-visible lg:overflow-y-auto custom-scrollbar"
            >
              <div className="p-6 sm:p-8 space-y-6">
                {/* Profile Brief */}
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="relative group">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gray-50 flex items-center justify-center text-[#885ABB] text-3xl font-bold border-4 border-white shadow-sm overflow-hidden">
                      {booking?.rawData?.customer?.user_image_url || booking?.rawData?.user?.user_image_url ? (
                        <img src={booking?.rawData?.customer?.user_image_url || booking?.rawData?.user?.user_image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        booking?.customerName?.charAt(0) || "U"
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#402B69]">
                      {booking?.customerName}
                    </h3>
                    <p className="text-[13px] text-gray-400 font-medium truncate max-w-[200px]">
                      {booking?.rawData?.customer?.email || booking?.rawData?.user?.email || "No email provided"}
                    </p>
                  </div>
                </div>

                {/* Sub-sections matching sidebar styling */}
                <div className="space-y-6 pt-2">
                  {/* CONTACT INFO */}
                  <div className="space-y-2.5">
                    <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Contact Info</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#F9F9F9] border border-transparent shadow-sm">
                        <Phone size={14} className="text-gray-400" />
                        <span className="text-sm font-semibold text-[#402B69]">
                          {booking?.rawData?.customer?.mobile || booking?.rawData?.customer?.phone || booking?.rawData?.user?.mobile || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#F9F9F9] border border-transparent shadow-sm">
                        <Mail size={14} className="text-gray-400" />
                        <span className="text-sm font-semibold text-[#402B69] truncate">
                          {booking?.rawData?.customer?.email || booking?.rawData?.user?.email || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* APPOINTMENT DETAILS */}
                  <div className="space-y-2.5">
                    <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Appointment Details</h4>
                    <div className="flex items-center justify-between p-3 rounded-2xl bg-[#885ABB]/5 text-[#885ABB] font-bold border border-transparent shadow-sm">
                      <div className="flex items-center gap-3">
                        <Clock size={14} />
                        <span className="text-sm">{bookingTime}</span>
                      </div>
                      <span className="text-[10px] uppercase opacity-70">Slot</span>
                    </div>
                  </div>

                  {/* STYLIST Addition */}
                  <div className="space-y-2.5">
                    <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Stylist</h4>
                    <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#F9F9F9] border border-transparent shadow-sm">
                      <User size={14} className="text-gray-400" />
                      <span className="text-sm font-semibold text-[#402B69]">
                        {(() => {
                          // Show all unique per-item stylists if available, else booking-level name
                          const perItemNames = items
                            .map((item: any) => {
                              const empId = item.employee_id || item.employee?.id;
                              return (empId && employeeLookup[Number(empId)]) || item.employee?.name || null;
                            })
                            .filter(Boolean);
                          const unique = Array.from(new Set(perItemNames));
                          return unique.length > 0
                            ? unique.join(", ")
                            : (booking?.employeeName || "Not assigned");
                        })()}
                      </span>
                    </div>
                  </div>

                  {/* MANAGE STATUS */}
                  <div className="space-y-2.5">
                    <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Status</h4>
                    <div className={cn(
                      "flex items-center justify-between p-3 rounded-2xl font-bold text-sm border shadow-sm",
                      booking?.status?.toLowerCase() === "completed"
                        ? "bg-green-50 text-green-700 border-green-100"
                        : "bg-blue-50 text-blue-700 border-blue-100"
                    )}>
                      <div className="flex items-center gap-3">
                        <CheckCircle2 size={14} />
                        <span className="capitalize">{booking?.status || "Booked"}</span>
                      </div>
                      <span className="text-[10px] uppercase opacity-70">
                        {booking?.status?.toLowerCase() === "completed" ? "Final" : "Slot"}
                      </span>
                    </div>
                  </div>

                  {/* CONSULTATION FORMS */}
                  {booking?.consultation_forms && booking.consultation_forms.length > 0 && (
                    <div className="space-y-2.5">
                      <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Consultation Forms</h4>
                      <div className="flex flex-wrap gap-2">
                        {booking.consultation_forms.map((form) => (
                          <a
                            key={form.id}
                            href={`/consultation-response/${form.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                              "px-3 py-1.5 rounded-xl text-[11px] font-bold uppercase border flex items-center gap-1.5 hover:opacity-80 transition-all shadow-sm w-full",
                              form.submitted_at
                                ? "bg-green-50 text-green-700 border-green-100"
                                : "bg-yellow-50 text-yellow-700 border-yellow-100"
                            )}
                          >
                            <span className="flex-1 truncate">{form.form.title}</span>
                            {form.submitted_at && <CheckCircle2 size={12} />}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* RIGHT PANEL - Precise SummaryStep replication */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex-1 flex flex-col h-auto lg:h-full overflow-visible lg:overflow-hidden relative"
            >
              {/* Internal Header for Right Panel */}
              <div className="px-4 lg:px-6 pt-4 lg:pt-8 pb-3 lg:pb-5 bg-white border-b border-gray-100 shadow-sm relative z-20">
                <div className="flex items-center justify-between mb-3 lg:mb-4">
                  <div>
                    <h2 className="text-lg lg:text-[22px] font-bold text-[#402B69] leading-tight">
                      Appointment Details
                    </h2>
                    <div className="flex items-center gap-2 mt-0.5 lg:mt-1">
                      <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                        Appointment Info
                      </span>
                    </div>
                  </div>
                </div>


              </div>

              {/* Main Content matching SummaryStep classes */}
              <div className="flex-1 overflow-visible lg:overflow-y-auto custom-scrollbar">
                <div className="space-y-6 p-4 sm:p-6 pb-2">
                  <section className="space-y-4">
                    <div className="flex justify-between items-center px-1">
                      <h4 className="text-[11px] font-[600] text-[#402B69] uppercase tracking-wider opacity-60">
                        Services
                      </h4>
                      <span className="text-[10px] font-bold text-[#885ABB] bg-white border border-[#885ABB]/20 px-2.5 py-1 rounded-full">
                        {items.length} {items.length === 1 ? 'Selected' : 'Selected'}
                      </span>
                    </div>

                    <div className="space-y-2">
                      {items.map((item: any, idx) => {
                        const originalPrice = Number(item?.business_service?.price || item?.unit_price || 0);
                        const discountedPrice = Number(item?.unit_price || 0);
                        const hasDiscount = discountedPrice < originalPrice;

                        // Determine duration text (e.g., "2 hours" or "120 minutes")
                        const durationValue = item?.business_service?.time || "N/A";
                        const durationType = item?.business_service?.time_type || "";
                        const durationText = `${durationValue} ${durationType}`.trim();

                        return (
                          <div
                            key={item.id || idx}
                            className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col gap-3 shadow-sm"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-1.5 h-8 bg-[#885ABB] rounded-full" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-[14px] font-[600] text-[#402B69] truncate uppercase tracking-tight">
                                    {item.name || item.business_service?.name || item.product?.name || "Line item"}
                                  </p>
                                  <div className="flex items-center gap-2 text-[10px] text-gray-400 font-medium mt-0.5">
                                    <Clock size={10} /> <span>{durationText}</span>
                                    <span className="w-1 h-1 rounded-full bg-gray-300 mx-0.5" />
                                    <User size={10} />{" "}
                                    <span className="truncate">
                                      {(() => {
                                        // 1. Resolve from item.employee_id via booking employees lookup
                                        const empId = item.employee_id || item.employee?.id;
                                        if (empId && employeeLookup[Number(empId)]) {
                                          return employeeLookup[Number(empId)];
                                        }
                                        // 2. Direct employee object on item
                                        if (item.employee?.name) return item.employee.name;
                                        // 3. Fallback to booking-level name (last resort)
                                        return booking?.employeeName || "Not assigned";
                                      })()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex flex-col items-end">
                                  {hasDiscount ? (
                                    <>
                                      <span className="text-[10px] font-bold text-gray-400 line-through">
                                        {currency}{originalPrice.toFixed(2)}
                                      </span>
                                      <span className="text-base font-bold text-[#885ABB] whitespace-nowrap">
                                        {currency}{discountedPrice.toFixed(2)}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-base font-bold text-[#885ABB] whitespace-nowrap">
                                      {currency}{originalPrice.toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                </div>
              </div>

              {/* Footer matching SummaryStep classes */}
              <div className="bg-white shadow-[0_-10px_30px_rgba(0,0,0,0.03)] border-t border-gray-100 flex flex-col w-full z-10 sticky bottom-0 lg:static">
                <div className="px-4 sm:px-6 pt-4 pb-12 sm:pb-8">
                  <section className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm space-y-3">
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs text-gray-500 font-medium">
                        <span>Total Amount</span>
                        <span>
                          {currency}
                          {totalAmount.toFixed(2)}
                        </span>
                      </div>

                      <div className="flex justify-between text-xs text-gray-500 font-medium">
                        <span>Taxes</span>
                        <span>{currency}{totalTax.toFixed(2)}</span>
                      </div>

                      <div className="flex justify-between text-xs text-green-600 font-medium">
                        <span>Total Paid</span>
                        <span>{currency}{totalPaidAmount.toFixed(2)}</span>
                      </div>

                      <div className="pt-3 border-t border-gray-50 flex justify-between items-center">
                        <span className="text-sm sm:text-base font-[600] text-[#402B69]">
                          {booking?.status?.toLowerCase() === "completed" ? "Total Paid" : "Total Due"}
                        </span>
                        <span className="text-lg sm:text-xl font-bold text-[#885ABB]">
                          {currency}
                          {booking?.status?.toLowerCase() === "completed"
                            ? totalPaidAmount.toFixed(2)
                            : totalDueAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </>
  );
};

export default BookingDetailsDrawer;
